import React, { useEffect, useRef, useState } from "react";

export enum VideoExtensionType {
  MP4,
  MOV,
}

type VideoPlayerProps = {
  src: string;
  smallSrc: string;
  poster?: string;
  smallPoster?: string;
  onEnded?: () => void;
  className?: string;
  smallClassName?: string;
};

const VideoPlayer = ({
  src,
  smallSrc,
  poster,
  smallPoster,
  onEnded,
  className = "",
  smallClassName = "",
}: VideoPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // Callback ref: runs synchronously during commit, BEFORE the browser makes
  // its initial autoplay attempt. React doesn't reliably set the `muted`
  // *property* from the JSX attribute, so we set it here — iOS only autoplays
  // videos it considers muted at the moment it first evaluates them. Setting it
  // later (in useEffect, after paint) is too late: the first autoplay has
  // already been rejected and the play button painted.
  const setVideoRef = (node: HTMLVideoElement | null) => {
    if (node) {
      node.muted = true;
      node.defaultMuted = true;
    }
    videoRef.current = node;
  };
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== "undefined" ? window.innerWidth < 640 : false,
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const videoSource = isMobile ? smallSrc : src;
  const videoPoster = isMobile ? smallPoster : poster;
  const videoClass = isMobile ? smallClassName : className;

  // Overlay the poster as a plain <img> until playback actually starts. iOS
  // paints its own play-button glyph over any paused inline <video> (a `poster`
  // attribute doesn't remove it) — a real <img> has no such overlay, so Low
  // Power Mode users see a clean still frame instead of a play button.
  const [posterVisible, setPosterVisible] = useState<boolean>(!!videoPoster);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // muted is already set synchronously by setVideoRef; belt-and-suspenders
    // in case the node was reused without re-running the callback ref.
    video.muted = true;

    // Show the poster again whenever the source swaps (mobile/desktop), until
    // the new clip reports it's playing.
    setPosterVisible(!!videoPoster);

    let handleInteraction: (() => void) | null = null;
    // Force autoplay retry after load
    const tryPlay = async () => {
      try {
        await video.play();
      } catch (err) {
        console.info("Autoplay blocked; will retry after user interaction.");
        handleInteraction = async () => {
          await video.play();
          document.removeEventListener("touchstart", handleInteraction!);
          document.removeEventListener("click", handleInteraction!);
        };
        document.addEventListener("touchstart", handleInteraction);
        document.addEventListener("click", handleInteraction);
      }
    };
    tryPlay();

    return () => {
      if (handleInteraction) {
        document.removeEventListener("touchstart", handleInteraction);
        document.removeEventListener("click", handleInteraction);
      }
    };
  }, [videoSource, videoPoster]);

  return (
    <div className={`video-container ${videoClass}`}>
      <video
        ref={setVideoRef}
        key={videoSource}
        src={videoSource}
        poster={videoPoster}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        controls={false}
        onEnded={onEnded}
        onPlaying={() => setPosterVisible(false)}
      />
      {videoPoster && posterVisible && (
        <img
          src={videoPoster}
          alt=""
          aria-hidden="true"
          className="video-poster"
        />
      )}
    </div>
  );
};

export default VideoPlayer;
